import {
  resultCacheKey,
  useData,
  useHasQuizResults,
} from '@mentimeter/core-hooks';
import { isTypeQuizSlide } from '@mentimeter/editor-essentials/calculations/question-filters';
import {
  core,
  type QuestionRange,
  type SessionResult,
} from '@mentimeter/http-clients';
import { usePresentationRealtimeStore } from '@mentimeter/realtime';
import type { QuestionWithSlide } from '@mentimeter/editor-schema/api-types-overrides';
import { useCallback, useEffect, useMemo } from 'react';
import debounce from 'lodash/debounce';
import type { DebouncedFunc } from 'lodash';

export const POLLING_INTERVAL = 10000;

interface Result {
  result: SessionResult | undefined;
  range: QuestionRange;
}

interface ActiveResults {
  hasRespondents: boolean;
  result: Result['result'];
  isPlaceholderResults: boolean;
  revalidate: (props?: { debounce: { enabled: boolean } }) => void;
}

export const useEditorResults = ({
  question,
  sessionId,
  seriesId,
}: {
  question: QuestionWithSlide | undefined | null;
  sessionId: number | 'current';
  seriesId: string;
}): ActiveResults => {
  const questionId = question?.id;
  const connectionState = usePresentationRealtimeStore(
    (state) => state.connectionState,
  );

  const hasQuizResults = useHasQuizResults({
    questionId: question?.id,
    questionType: question?.type,
    shouldFetch: isTypeQuizSlide(question?.type),
  });

  const cacheKey = questionId
    ? resultCacheKey(questionId, sessionId)
    : undefined;

  const resultsData = useData<SessionResult | undefined>(
    {
      cacheKey,
      seriesId,
      fetcher: async (...arg) => {
        if (!questionId) {
          return;
        }
        const { data } = await core().questions.result.get(
          questionId,
          typeof sessionId === 'number' ? { session: sessionId } : undefined,
        );
        return data;
      },
    },
    {
      refreshInterval:
        connectionState === 'failed' || connectionState === 'disconnected'
          ? POLLING_INTERVAL
          : 0,
    },
  );

  const data = resultsData?.data ?? question?.result;
  const isPlaceholderResults = !resultsData?.data;

  const hasRespondents =
    (data?.results && data?.respondents > 0) || hasQuizResults;

  const { revalidate } = resultsData;

  const debouncedRevalidate = useMemo<DebouncedFunc<typeof revalidate>>(() => {
    return debounce(revalidate, 400, {
      maxWait: 1000,
    });
  }, [revalidate]);

  useEffect(() => {
    return () => {
      debouncedRevalidate.cancel();
    };
  }, [debouncedRevalidate]);

  const optionallyDebouncedRevalidate = useCallback(
    (props?: { debounce: { enabled: boolean } }) => {
      if (props?.debounce?.enabled === true) {
        debouncedRevalidate();
      } else {
        revalidate();
      }
    },
    [revalidate, debouncedRevalidate],
  );

  return {
    revalidate: optionallyDebouncedRevalidate,
    result: data,
    hasRespondents,
    isPlaceholderResults,
  };
};
